import { useState, useEffect } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

// Material UI
import SimpleModal from '../components/SimpleModal';
import DocumentNavigation from '../components/DocumentNavigation';
import DocumentBrowser from '../components/DocumentBrowser';
import dayjs from 'dayjs';





const DocumentManager = ({ documents, accessKey, sessionEndTime, propertyName, hideHeader, toggleHeader }) => {
  const navigate = useNavigate();
  const [currentFolderName, setCurrentFolderName] = useState('Documents');
  const [breadcrumbs, setBreadcrumbs] = useState([{ name: 'Documents', id: null }]);

  // console.log("DOCUMENTS: ", documents)

  const [errorMessage, setErrorMessage] = useState(null);
  const [page, setPage] = useState(1);
  const itemsPerPage = 100;
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [searchData, setSearchData] = useState('');
  const [entriesPage, setEntriesPage] = useState(null);
  const [filteredEntries, setFilteredEntries] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);



  const queryClient = useQueryClient();

  const fetchMoreItems = async (path, currentItems = [], skipCount = 0) => {
    const response = await fetch(
      `${import.meta.env.VITE_API_URL}/list-documents?path=${encodeURIComponent(path)}&skipCount=${skipCount}`,
      {
        headers: {
          'X-Signature': accessKey
        },
      }
    );
    const res = await response.json();
    const newItems = [...currentItems, ...res.data.entries];
    
    if (res.data.pagination.hasMoreItems) {
      return fetchMoreItems(path, newItems, skipCount + 500);
    }
    
    return newItems;
  };

  const { data: currentFolder, isLoading } = useQuery({
    queryKey: [documents.entries[0].rootFolder, breadcrumbs.slice(1).map(breadcrumb => breadcrumb.name).join('/')],
    queryFn: async () => {
      if (dayjs(sessionEndTime).isBefore(dayjs())) {
        console.log("Session expired")
        setErrorMessage("Your session has expired. You will now be redirected to the request form.");
        return null;
      }
      
      if (currentFolderName === 'Documents') {
        setTotalItems(documents?.pagination?.totalItems);
        // Just return the documents prop as-is since it already has the correct structure
        return documents;
      }

      // console.log(currentFolderName, breadcrumbs)
      let newPath = breadcrumbs.slice(1).map(breadcrumb => breadcrumb.name).join('/');
      // console.log(newPath)

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/list-documents?path=${encodeURIComponent(`/${newPath}`)}&skipCount=0`,
        {
          headers: {
            'X-Signature': accessKey
          },
        }
      );
      const res = await response.json();
      // console.log(res);
      setTotalItems(res?.data?.pagination?.totalItems && `${res?.data?.pagination?.totalItems} items`);
      
      // Get initial entries
      const entries = res.data.entries;
      
      // If there are more items, fetch them in the background
      if (res.data.pagination.hasMoreItems) {
        setIsLoadingMore(true);
        fetchMoreItems(`/${newPath}`, entries, 500)
          .then(allEntries => {
            // Update the query data with all entries
            queryClient.setQueryData([documents.entries[0].rootFolder, newPath], {
              ...res.data,
              entries: allEntries.sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
            });
            setIsLoadingMore(false);
          });
      }

      // Return initial data immediately
      return {
        ...res.data,
        entries: entries.sort((a, b) => dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf())
      };
    },
    enabled: true
  });

  // 



  const handleFolderClick = (folderName, folderId) => {
    console.log("Documents", documents)
    setCurrentFolderName(folderName);
    setBreadcrumbs([...breadcrumbs, {
      name: folderName,
      id: folderId,
      folder: null // Will be populated by the query
    }]);
    setPage(1); // Reset to first page when changing folders
  };




  useEffect(() => {
    // console.log("HERE: ", currentFolder)
    if (currentFolder?.entries) {
      // Filter entries based on search data
      const filtered = currentFolder?.entries?.filter(item =>
        (item.name + ' ' + dayjs(item.createdAt).format('DD/MM/YYYY hh:mm A') + ' Lot:' + item.lotNumber || '').toLowerCase().includes(searchData.toLowerCase())
      ) || [];

      setFilteredEntries(filtered);
      // console.log("FILTERED: ", filtered)

      // Calculate pagination
      const totalFilteredPages = Math.ceil(filtered.length / itemsPerPage);
      setTotalPages(totalFilteredPages);
      setPage(1);
      if (searchData.length > 0) {
        setTotalItems(`${filtered.length} of ${currentFolder?.pagination?.totalItems} items`);
      } else {
        setTotalItems(`${currentFolder?.pagination?.totalItems} items`);
      }
    }
  }, [searchData, currentFolder?.entries, itemsPerPage]);

  useEffect(() => {
    if (filteredEntries) {
      // console.log("FILTERED ENTRIES: ", filteredEntries)
      // Calculate pagination
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setEntriesPage(filteredEntries?.slice(startIndex, endIndex) || []);
    }
  }, [page, filteredEntries])


  // if (isLoading) {
  //   return <Box sx={{ p: 2 }}>Loading...</Box>;
  // }
  const navigateHome = () => {
    navigate('/');
  }

  return (
    <>

      <SimpleModal inProgress={isLoading} text={"Loading..."} />
      <SimpleModal inProgress={errorMessage !== null} text={errorMessage} buttonFunction={() => navigateHome()} />

      <DocumentNavigation 
          propertyName={propertyName}
          setCurrentFolderName={setCurrentFolderName}
          page={page}
          setPage={setPage}
          totalItems={totalItems}
          totalPages={totalPages}
          searchData={searchData}
          setSearchData={setSearchData}
          setBreadcrumbs={setBreadcrumbs}
          breadcrumbs={breadcrumbs}
          isLoadingMore={isLoadingMore}
          hideHeader={hideHeader}
          toggleHeader={toggleHeader}
        />
      <DocumentBrowser
        handleFolderClick={handleFolderClick}
        entriesPage={entriesPage}
        accessKey={accessKey}
        hideHeader={hideHeader}
      />
    </>
  );
};

export default DocumentManager;